import {BaseElement} from "back/commons/basis";
import {ActionBtn, ButtonActions, OButtonActionsInit, OButtonInit} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {LoginDialog} from "back/core/dialogs/login";
import {Action} from "lib/commons/actions";
import {IReg, REG} from 'lib/commons/registry';
import {DOMSH} from "lib/commons/xml/domsh";
import {IBasicUniversePointer, IUniverseEnv} from "lib/core/universe";
import {JUser, USER} from "lib/core/user";
import {JSX} from "lib/commons/xml/dom";
import {UserProfile} from "back/core/dialogs/userProfile";
import {UserPrefEditor} from "back/core/dialogs/userPrefEditor";
import showDialog = POPUP.showDialog;

export interface LoggedUser extends BaseElement {
	initialize(init: OLoggedUserInit): this
}

export interface OLoggedUserInit extends OButtonInit {}

export class LoggedUser extends BaseElement {

	defaultIcon?: string;

	protected connectBtn: ActionBtn<Element>;

	protected userBtn: ButtonActions<Element>;

	reg: IReg<IUniverseEnv>;

	protected _initialize(init: OLoggedUserInit) {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		const auth = this.reg.env.universe.auth;
		if (!auth.config.noAuthentication) {
			this._initAndInstallSkin(this.localName, init);
			this.defaultIcon = init.icon;
			this.connectBtn = new ActionBtn();
			const connectInit = Object.create(init || null);
			connectInit.action = this.reg.getSvc('userLogin') as Action;
			sr.appendChild(this.connectBtn.initialize(connectInit));
			const actionsInit = Object.create(init) as OButtonActionsInit<LoggedUser>;
			actionsInit.groupOrder = "me * logout";
			actionsInit.actions = this.reg.getList("actions:loggedUser");
			actionsInit.actionContext = this;
			this.userBtn = new ButtonActions<Element>().initialize(actionsInit);
			sr.appendChild(this.userBtn);
			auth.listeners.on('loggedUserChanged', (oldUser, newUser) => {this.setNewUser(newUser)}, 100); //Après les différents chargements
			this.setNewUser(auth.currentUser);
		}
	}

	protected setNewUser(newUser: JUser | null) {
		if (newUser) {
			this.connectBtn.hidden = true;
			this.userBtn.hidden = false;
			this.userBtn.label = USER.getPrimaryName(newUser);
			this.userBtn.title = USER.getLongName(newUser);
			this.userBtn.icon = this.defaultIcon || "/@skin@/core/objects/user.svg"; //TODO avatar si module actif
		} else {
			this.userBtn.hidden = true;
			this.connectBtn.hidden = false;
		}
	}
}

REG.reg.registerSkin('c-loggeduser', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
	}
`);

customElements.define('c-loggeduser', LoggedUser);

/**
 *
 */
REG.reg.registerSvc('userLogin', 1, new Action<Element>('userLogin')
	.setLabel("Se connecter...")
	.setIcon("/@skin@/core/widgets/loggedUser/login.svg")
	.setVisible((ctx: Element) => {
		const auth = (REG.findReg(ctx).env as IBasicUniversePointer).universe.auth;
		return auth.currentAuthenticatedUser == null;
	})
	.setExecute((ctx: Element) => {
		const dialog = POPUP.showDialog(new LoginDialog(), ctx, {fixSize: false, titleBar: {barLabel: {label: "Connexion"}}});
		const universe = (REG.findReg(ctx).env as IBasicUniversePointer).universe;
		const onUserChange = () => {dialog.close()};
		universe.auth.listeners.on('loggedUserChanged', onUserChange);
		dialog.onNextClose().then(() => {universe.auth.listeners.removeListener('loggedUserChanged', onUserChange)})
	}));

REG.reg.registerSvc('userLogout', 1, new Action<Element>('userLogout')
	.setGroup("logout")
	.setLabel("Se déconnecter")
	.setVisible((ctx: Element) => {
		const auth = (REG.findReg(ctx).env as IBasicUniversePointer).universe.auth;
		return auth.currentAuthenticatedUser != null;
	})
	.setExecute((ctx: Element) => {
		const userSelf = (REG.findReg(ctx).env as IBasicUniversePointer).universe.userSelf;
		userSelf.logout();
	}));

REG.reg.registerSvc('userProfile', 1, new Action<Element>('userProfile')
	.setGroup("me")
	.setLabel("Mon profil...")
	.setVisible((ctx: Element) => {
		const auth = (REG.findReg(ctx).env as IBasicUniversePointer).universe.auth;
		return auth.currentAuthenticatedUser != null;
	})
	.setExecute(async (ctx: Element) => {
		const reg = REG.findReg(ctx) as IReg<IBasicUniversePointer>;
		const {UserProfile} = await import("back/core/dialogs/userProfile.js");
		showDialog(new UserProfile().initialize({reg}), ctx, {titleBar: {barLabel: {label: "Mon profil"}}, fixSize: false});
	}));

REG.reg.registerSvc('userPref', 1, new Action<Element>('userPref')
	.setGroup("me")
	.setLabel("Mes préférences...")
	.setVisible((ctx: Element) => {
		const auth = (REG.findReg(ctx).env as IBasicUniversePointer).universe.auth;
		return auth.currentAuthenticatedUser != null;
	})
	.setExecute(async (ctx: Element) => {
		const reg = REG.findReg(ctx) as IReg<IBasicUniversePointer>;
		const {UserPrefEditor} = await import("back/core/dialogs/userPrefEditor.js");
		const body = new UserPrefEditor().initialize({reg, prefEditorsList: "user:prefs"});
		await body.initializedAsync;
		showDialog(body, ctx, {titleBar: "Mes préférences", resizer:{}});
	}));

REG.reg.addSvcToList("actions:loggedUser", 'userProfile', 1, 'userProfile', 10);
REG.reg.addSvcToList("actions:loggedUser", 'userPref', 1, 'userPref', 20);
REG.reg.addSvcToList("actions:loggedUser", 'userLogout', 1, 'userLogout', 110);
